import { Component, ChangeDetectionStrategy, Input, signal, computed, effect, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslationModule, TranslatePipe } from 'i18n';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'app-countdown-timer',
  standalone: true,
  imports: [CommonModule, TranslationModule, TranslatePipe],
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountdownTimerComponent {
  // ────────────────────────────────────────────────
  // Inputs & Outputs
  // ────────────────────────────────────────────────
  @Input({ required: true }) targetDate!: Date;
  @Input() message: string = 'orders_start_at';

  // ────────────────────────────────────────────────
  // Reactive state (signals)
  // ────────────────────────────────────────────────
  countdown = signal<CountdownTime>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  isExpired = signal(false);

  // Formatted target date (reactive to language/timezone)
  formattedDate = computed(() => {
    return this.targetDate.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  });

  private destroyRef = inject(DestroyRef);
  private intervalId: any = null;

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Start countdown on component creation
    effect(() => {
      this.updateCountdown();
      this.intervalId = setInterval(() => this.updateCountdown(), 1000);

      // Cleanup interval when component is destroyed
      this.destroyRef.onDestroy(() => {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      });
    });
  }

  // ────────────────────────────────────────────────
  // Countdown logic
  // ────────────────────────────────────────────────
  private updateCountdown(): void {
    const now = Date.now();
    const target = this.targetDate.getTime();
    const distance = target - now;

    if (distance < 0) {
      this.isExpired.set(true);
      this.countdown.set({ days: 0, hours: 0, minutes: 0, seconds: 0 });

      // Stop interval once expired
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }

    this.countdown.set({
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    });
  }

  // Helper for padding zeros (used in template)
  pad(value: number): string {
    return value.toString().padStart(2, '0');
  }
}